import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { sessionNeedsMfa, verifyTotpCode, isTransientAuthError } from '@tradeoutlet/shared';
import { api, setMfaRequiredHandler, setTokenGetter, setUnauthorizedHandler } from './api';
import { supabase } from './supabase';
import { queryClient } from './queryClient';
import { messageStore } from './messageStore';
import { runOutbox } from './runOutbox';
import { clockOutbox } from './clockOutbox';

const DEV_KEY = 'collab.devSession';
/** Set when the session ran out (t269u); Sign in shows "Your session has expired" once. */
export const SESSION_EXPIRED_KEY = 'collab.sessionExpired';

interface DevSession { access_token: string; user_id: string; expires_at: number }

/** 'mfa' (t413u): password done, the two-factor code still to enter. Only the code step shows. */
export type AuthStatus = 'loading' | 'signed_out' | 'mfa' | 'signed_in';

interface AuthContextValue {
  status: AuthStatus;
  userId: string | null;
  mode: 'supabase' | 'dev';
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  devSignIn: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Supabase reset flow: emails a link to /reset-password (needs SMTP on Supabase Auth). */
  requestPasswordReset: (email: string) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  /** t413u: the code step. Resolves false for a wrong code; throws for network/rate limits. */
  verifyMfa: (code: string) => Promise<boolean>;
  /** t413u: a recovery code instead (turns two-factor off for the account; set it up again after). */
  recoverMfa: (code: string) => Promise<void>;
}

const statusOf = (s: Session | null): AuthStatus => (s ? (sessionNeedsMfa(s) ? 'mfa' : 'signed_in') : 'signed_out');

const AuthContext = createContext<AuthContextValue | null>(null);

function readDev(): DevSession | null {
  try {
    const s = JSON.parse(localStorage.getItem(DEV_KEY) ?? 'null') as DevSession | null;
    return s && s.expires_at > Date.now() + 60_000 ? s : null;
  } catch { return null; }
}

/**
 * Authentication. Production uses Supabase Auth (email/password + Microsoft Entra via
 * the 'azure' provider). Local development without Supabase uses the API's dev-only
 * token endpoint. Tokens never leave memory/localStorage except as a Bearer header.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const mode: 'supabase' | 'dev' = supabase ? 'supabase' : 'dev';
  const [session, setSession] = useState<Session | null>(null);
  const [dev, setDev] = useState<DevSession | null>(() => (mode === 'dev' ? readDev() : null));
  const [status, setStatus] = useState<AuthStatus>(mode === 'dev' ? (readDev() ? 'signed_in' : 'signed_out') : 'loading');
  const sessionRef = useRef<Session | null>(null);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      sessionRef.current = data.session;
      setSession(data.session);
      setStatus(statusOf(data.session));
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      sessionRef.current = s;
      setSession(s);
      setStatus(statusOf(s));
      if (s) supabase!.realtime.setAuth(s.access_token);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const getAccessToken = useCallback(async () => {
    if (supabase) {
      const s = sessionRef.current;
      if (s && (s.expires_at ?? 0) * 1000 - Date.now() > 30_000) return s.access_token;
      const { data } = await supabase.auth.getSession(); // refreshes if needed
      sessionRef.current = data.session;
      return data.session?.access_token ?? null;
    }
    return readDev()?.access_token ?? null;
  }, []);

  // Register synchronously during render: child queries fire in their own effects, which run
  // before this provider's effects, so a useEffect here would send the first requests unauthenticated.
  setTokenGetter(getAccessToken);

  // t269u: the API said the session is no longer valid. Try one refresh; if there's no session to be had, sign out on
  // this device only and land on Sign in with "Your session has expired". Unlike signOut() this keeps the outboxes,
  // so unsent messages and clock/run steps go out once the same person signs back in.
  const expiring = useRef(false);
  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (expiring.current) return;
      expiring.current = true;
      void (async () => {
        try {
          if (supabase) {
            const { data, error } = await supabase.auth.refreshSession();
            if (!error && data.session) return;
            sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
            await supabase.auth.signOut({ scope: 'local' });
          } else {
            sessionStorage.setItem(SESSION_EXPIRED_KEY, '1');
            localStorage.removeItem(DEV_KEY);
            setDev(null);
            setStatus('signed_out');
          }
          queryClient.clear();
        } catch { /* the next request tries again */ } finally {
          setTimeout(() => { expiring.current = false; }, 5_000);
        }
      })();
    });
    return () => setUnauthorizedHandler(() => undefined);
  }, []);

  // t413u: a session from before this account turned two-factor on (another device set it up) gets 403 mfa_required:
  // refresh (the new token says what the account has now) and show the code step.
  useEffect(() => {
    setMfaRequiredHandler(() => {
      if (!supabase) return;
      void supabase.auth.refreshSession().then(({ data }) => {
        const s = data.session;
        sessionRef.current = s;
        setSession(s);
        setStatus(statusOf(s));
      }).catch(() => undefined);
    });
    return () => setMfaRequiredHandler(() => undefined);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    mode,
    userId: mode === 'supabase' ? session?.user.id ?? null : dev?.user_id ?? null,
    getAccessToken,
    async signInWithPassword(email, password) {
      if (!supabase) throw new Error('Supabase is not configured');
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    async signInWithMicrosoft() {
      if (!supabase) throw new Error('Supabase is not configured');
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: { scopes: 'openid profile email offline_access', redirectTo: window.location.origin },
      });
      if (error) throw error;
    },
    async devSignIn(email) {
      const r = await api.devLogin(email);
      const s: DevSession = { access_token: r.access_token, user_id: r.user_id, expires_at: Date.now() + r.expires_in * 1000 };
      localStorage.setItem(DEV_KEY, JSON.stringify(s));
      setDev(s);
      setStatus('signed_in');
    },
    async verifyMfa(code) {
      if (!supabase) return false;
      const ok = await verifyTotpCode(supabase.auth, code);
      // supabase-js swaps in the aal2 session and fires MFA_CHALLENGE_VERIFIED; onAuthStateChange moves us on.
      return ok;
    },
    async recoverMfa(code) {
      if (!supabase) throw new Error('Supabase is not configured');
      await api.mfaRecover(code);
      const { data, error } = await supabase.auth.refreshSession();
      if (error && isTransientAuthError(error)) throw error;
      sessionRef.current = data.session;
      setSession(data.session);
      setStatus(statusOf(data.session));
      queryClient.clear();
    },
    async requestPasswordReset(email) {
      // Sent by the Hub's own email (Settings > Email) with a branded link; the API falls back to Supabase Auth's mailer.
      await api.requestPasswordReset(email);
    },
    async signOut() {
      // docs/offline.md §3.7: sign-out wipes what this device kept for the user (unsent messages included).
      await messageStore.forgetUser().catch(() => undefined); // wipes the user's whole outbox table (run ops too)
      runOutbox.forget();
      clockOutbox.forget();
      if (supabase) await supabase.auth.signOut();
      localStorage.removeItem(DEV_KEY);
      setDev(null);
      setStatus('signed_out');
      queryClient.clear();
    },
  }), [status, mode, session, dev, getAccessToken]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
